/**
 * useCanvasControls.js
 *
 * Reusable canvas viewport controls — zoom (mouse wheel + pinch + buttons),
 * pan (drag + spacebar hand tool), and fit-to-content.
 *
 * Returns state and handlers; the consumer wires them to a wrapper <div>
 * containing absolute-positioned content with `transform: translate(pan.x,
 * pan.y) scale(zoom)`.
 *
 * Coordinate model:
 *   - The wrapper is screen-space (Y-down pixels, origin at wrapper's top-left).
 *   - The content is "world-space". For SelectionCanvas this is image pixels;
 *     for LayoutView this is sheet mm. The hook is unit-agnostic — it just
 *     does the affine transform.
 *
 * Inverse mapping:
 *   worldX = (wrapX - pan.x) / zoom
 *   worldY = (wrapY - pan.y) / zoom
 *
 * Wheel zoom is anchored at the cursor; the same world point under the
 * cursor stays under the cursor across the zoom step. Trackpad pinch is
 * handled identically (scaled by deltaY magnitude).
 */

import { useState, useRef, useCallback, useEffect } from 'react';

const DEFAULT_ZOOM_MIN = 0.1;
const DEFAULT_ZOOM_MAX = 20;
// Single-event deltaY clamp — prevents one outlier wheel event from teleporting zoom
const WHEEL_DELTA_CLAMP_PX = 200;
// Per-pixel zoom rate. 1 / (1 / 0.0015) ≈ 666 px to double zoom. Tuned for trackpad+mouse parity.
const WHEEL_ZOOM_RATE = 0.0015;

export function useCanvasControls({
  zoomMin = DEFAULT_ZOOM_MIN,
  zoomMax = DEFAULT_ZOOM_MAX,
  initialZoom = 1,
  initialPan = { x: 0, y: 0 },
  // If true, holding spacebar activates hand-tool drag-to-pan even from inside
  // child elements (without interfering with their click handlers).
  enableSpacebarPan = true,
} = {}) {
  const [zoom, setZoomState] = useState(initialZoom);
  const [pan, setPan] = useState(initialPan);
  const [isPanning, setIsPanning] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);

  const wrapperRef = useRef(null);

  // Refs for fresh reads inside callbacks that don't depend on state
  const zoomRef = useRef(initialZoom);
  const panRef = useRef(initialPan);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panRef.current = pan; }, [pan]);

  // Pan-drag bookkeeping
  const dragStateRef = useRef(null);

  // ────────────────────────────────────────────────────────────────────
  // Coordinate conversion
  // ────────────────────────────────────────────────────────────────────

  /** Convert a screen event's clientX/clientY to wrapper-local pixels. */
  const eventToWrapper = useCallback((e) => {
    const wrap = wrapperRef.current;
    if (!wrap) return null;
    const r = wrap.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }, []);

  /** Wrapper-local pixels → world coordinates. */
  const wrapperToWorld = useCallback((wrapX, wrapY) => {
    const z = zoomRef.current;
    const p = panRef.current;
    return { x: (wrapX - p.x) / z, y: (wrapY - p.y) / z };
  }, []);

  /** Event → world coordinates (combines the two above). */
  const eventToWorld = useCallback((e) => {
    const w = eventToWrapper(e);
    if (!w) return null;
    return wrapperToWorld(w.x, w.y);
  }, [eventToWrapper, wrapperToWorld]);

  // ────────────────────────────────────────────────────────────────────
  // Zoom
  // ────────────────────────────────────────────────────────────────────

  /**
   * Set zoom to `nz`, anchored at wrapper-local point (ax, ay) so the world
   * point under the anchor stays under the anchor. If the anchor is omitted,
   * the wrapper center is used.
   */
  const setZoomCentered = useCallback((nz, ax, ay) => {
    const wrap = wrapperRef.current;
    if (!wrap) return;
    const clamped = Math.max(zoomMin, Math.min(zoomMax, nz));
    const currentZoom = zoomRef.current;
    if (clamped === currentZoom) return;

    if (ax === undefined || ay === undefined) {
      ax = wrap.clientWidth / 2;
      ay = wrap.clientHeight / 2;
    }
    const ratio = clamped / currentZoom;
    setPan(prev => ({ x: ax - (ax - prev.x) * ratio, y: ay - (ay - prev.y) * ratio }));
    setZoomState(clamped);
  }, [zoomMin, zoomMax]);

  /** Multiplicative zoom step. Used by + / − buttons. */
  const zoomBy = useCallback((factor, ax, ay) => {
    setZoomCentered(zoomRef.current * factor, ax, ay);
  }, [setZoomCentered]);

  const zoomIn = useCallback(() => zoomBy(1.2), [zoomBy]);
  const zoomOut = useCallback(() => zoomBy(1 / 1.2), [zoomBy]);

  /** Wheel handler. Cursor-anchored zoom with magnitude-proportional steps. */
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const wrap = wrapperRef.current;
    if (!wrap) return;
    const r = wrap.getBoundingClientRect();
    const cx = e.clientX - r.left, cy = e.clientY - r.top;

    // Normalize across deltaMode (line/page) and clamp to prevent pinch storms
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 16;        // line → px
    else if (e.deltaMode === 2) dy *= 400;  // page → px
    dy = Math.max(-WHEEL_DELTA_CLAMP_PX, Math.min(WHEEL_DELTA_CLAMP_PX, dy));

    const factor = Math.exp(-dy * WHEEL_ZOOM_RATE);
    setZoomCentered(zoomRef.current * factor, cx, cy);
  }, [setZoomCentered]);

  // React 18 makes wheel listeners passive by default → preventDefault is a
  // no-op and we get console spam. Attach manually with { passive: false }.
  useEffect(() => {
    const wrap = wrapperRef.current;
    if (!wrap) return;
    wrap.addEventListener('wheel', onWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  // ────────────────────────────────────────────────────────────────────
  // Pan (drag-to-pan)
  // ────────────────────────────────────────────────────────────────────

  /**
   * Begin a pan-drag from the given event. Caller decides when to begin
   * (e.g. middle mouse, or always-on for LayoutView, or only when
   * spaceHeld for SelectionCanvas).
   */
  const beginPanDrag = useCallback((e) => {
    e.preventDefault?.();
    setIsPanning(true);
    dragStateRef.current = {
      startPan: { ...panRef.current },
      clientX: e.clientX,
      clientY: e.clientY,
    };
  }, []);

  // Window-level mousemove + mouseup while a pan-drag is active.
  useEffect(() => {
    if (!isPanning) return;
    const onMove = (e) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      const dx = e.clientX - ds.clientX;
      const dy = e.clientY - ds.clientY;
      setPan({ x: ds.startPan.x + dx, y: ds.startPan.y + dy });
    };
    const onUp = () => {
      setIsPanning(false);
      dragStateRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isPanning]);

  // ────────────────────────────────────────────────────────────────────
  // Spacebar hand-tool toggle
  // ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!enableSpacebarPan) return;
    const onDown = (e) => {
      // Don't hijack space when typing in inputs/textareas
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (e.code === 'Space') {
        e.preventDefault();
        setSpaceHeld(true);
      }
    };
    const onUp = (e) => {
      if (e.code === 'Space') setSpaceHeld(false);
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, [enableSpacebarPan]);

  // ────────────────────────────────────────────────────────────────────
  // Fit-to-content
  // ────────────────────────────────────────────────────────────────────

  /**
   * Fit content of size (contentW × contentH) inside the wrapper, with
   * `padding` fraction (default 5%) of the smaller dimension as margin.
   * Centers the content in the wrapper.
   */
  const fit = useCallback(({ contentW, contentH, padding = 0.05, maxZoom = zoomMax }) => {
    const wrap = wrapperRef.current;
    if (!wrap || !contentW || !contentH) return;
    const ww = wrap.clientWidth || 1;
    const wh = wrap.clientHeight || 1;
    const factor = (1 - padding * 2);
    const fitZoom = Math.min(ww / contentW, wh / contentH) * factor;
    const clamped = Math.max(zoomMin, Math.min(maxZoom, fitZoom));
    setZoomState(clamped);
    setPan({
      x: (ww - contentW * clamped) / 2,
      y: (wh - contentH * clamped) / 2,
    });
  }, [zoomMin, zoomMax]);

  // ────────────────────────────────────────────────────────────────────
  // Direct setters (escape hatch for callers that need them)
  // ────────────────────────────────────────────────────────────────────

  /** Set zoom directly (clamped, no anchor). */
  const setZoom = useCallback((nz) => {
    const clamped = Math.max(zoomMin, Math.min(zoomMax, nz));
    setZoomState(clamped);
  }, [zoomMin, zoomMax]);

  /** Reset to initial zoom + pan. */
  const reset = useCallback(() => {
    setZoomState(initialZoom);
    setPan(initialPan);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The transform string consumers should apply to the content element.
  const transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;

  return {
    // state
    zoom, pan, isPanning, spaceHeld,
    // refs (consumer attaches to its wrapper)
    wrapperRef,
    // commands
    setZoom, setPan, setZoomCentered, zoomBy, zoomIn, zoomOut, fit, reset,
    // pan-drag begin (consumer wires to mousedown when appropriate)
    beginPanDrag,
    // helpers
    eventToWrapper, eventToWorld, wrapperToWorld,
    // for CSS
    transform,
  };
}
